import * as React from 'react'
import { Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import type {
	LicenseContractType,
	LicenseTextType,
	RenderedLicenseTextType
} from '../../../generated/types'
import {
	StyledCard,
	StyledCardTitle,
	StyledCardSubtitle,
	StyledTextRow,
	StyledTextLabel,
	StyledSecondaryButton,
	StyledPrimaryButton
} from './styles'

const { Paragraph } = Typography

interface ContractCardProps {
	contract: LicenseContractType
	onPreviewClick: (licenceText: LicenseTextType) => void
}

export const ContractCard: React.FC<ContractCardProps> = ({
	contract,
	onPreviewClick
}) => {
	const { t } = useTranslation()

	// Safety checks and fallbacks for missing data
	const licenseMaster = contract.licenseVersion?.licenseMaster
	const licenseVersion = contract.licenseVersion
	const licenseTexts =
		licenseVersion?.licenseTexts?.filter(
			(text): text is LicenseTextType => text !== null
		) || []

	// Prefer backend-provided, contract-scoped rendered texts to avoid cache normalization issues
	const renderedByKey = React.useMemo(() => {
		const list = contract.renderedLicenseTexts?.filter(
			(text): text is RenderedLicenseTextType => text !== null
		)
		const map = new Map<string, string>()
		if (list && Array.isArray(list)) {
			list.forEach(item => {
				const key = item.isMainText ? 'main' : `att:${item.title || ''}`
				map.set(key, item.renderedText || '')
			})
		}
		return map
	}, [contract.renderedLicenseTexts])

	// If critical data is missing, log error and don't render
	if (!licenseMaster || !licenseVersion) {
		console.error('ContractCard: Missing critical data', { contract })
		return null
	}

	// Sort licenceTexts: main document first, then attachments alphabetically
	const sortedLicenceTexts = [...licenseTexts].sort((a, b) => {
		if (a.isMainText) return -1
		if (b.isMainText) return 1
		return a.title.localeCompare(b.title)
	})

	// Format date with fallback
	const formattedDate = licenseVersion.publishedAt
		? moment(licenseVersion.publishedAt).format('DD.MM.YYYY')
		: 'N/A'

	const cardTitle = (
		<div>
			<StyledCardTitle>{licenseMaster.name || 'Unbenannt'}</StyledCardTitle>
			<StyledCardSubtitle>
				Version {licenseVersion.version || 'N/A'} |{' '}
				{String(
					t('license.agreements.publishedDate', {
						date: formattedDate
					})
				)}
			</StyledCardSubtitle>
		</div>
	)

	return (
		<StyledCard title={cardTitle}>
			{licenseMaster.description && (
				<Paragraph>{licenseMaster.description}</Paragraph>
			)}
			<div>
				{sortedLicenceTexts.map(licenceText => {
					// Ensure we have valid data before rendering
					const attachmentTitle = licenceText.title || 'Unbenannt'
					const isUpdated =
						contract.documents?.some(
							document =>
								document?.isOutdated &&
								document.latestUpdatedText?.id === licenceText.id
						) || false

					return (
						<StyledTextRow key={licenceText.id}>
							<StyledTextLabel strong>
								{licenceText.isMainText
									? t('license.agreements.mainDocument')
									: t('license.agreements.attachment', {
											title: attachmentTitle
										})}
								{isUpdated ? t('license.contracts.updatedMarker') : ''}
							</StyledTextLabel>
							{licenceText.pdfUrl ? (
								<StyledSecondaryButton
									onClick={() => onPreviewClick(licenceText)}
								>
									{t('license.agreements.view')}
								</StyledSecondaryButton>
							) : (
								<StyledPrimaryButton
									type="primary"
									onClick={() => {
										const key = licenceText.isMainText
											? 'main'
											: `att:${licenceText.title || attachmentTitle}`
										const rendered =
											renderedByKey.get(key) ?? licenceText.renderedText ?? ''
										onPreviewClick({
											...licenceText,
											title: licenceText.title || attachmentTitle,
											renderedText: rendered
										})
									}}
								>
									{t('license.agreements.preview')}
								</StyledPrimaryButton>
							)}
						</StyledTextRow>
					)
				})}
			</div>
		</StyledCard>
	)
}
